import { Row } from "@components/VerticalTable"
import { GraphStoreType } from "@features/graph/Graph"
import CoefficientsTable from "@features/graph/details/components/DeterministicNodeDetails/CoefficientsTable"
import {
  getExpressionWithCoefficients,
  getUpdatedExpression,
} from "@features/graph/details/components/DeterministicNodeDetails/helpers"
import { NodeProperties } from "@features/graph/details/components/PropertiesTable"
import { GraphEditMode } from "@features/graph/graphViewModel"
import { graphReplaceNode } from "@features/graph/state/graphReplaceNode"
import { deepCopy } from "@features/graph/utils/graphUtils"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material"
import React, { SyntheticEvent, useEffect, useState } from "react"
import { Node, useStoreApi } from "reactflow"
import { findNode } from "utils/graph"
import { ParentChildDetails } from "@features/graph/details/components/ParentChildDetails/ParentChildDetails"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { useLocalStorage } from "usehooks-ts"
import { NodeRowsAccordion } from "@features/graph/details/components/NodeRowsAccordion"
import { Android12Switch } from "@features/graph/details/components/DeterministicNodeDetails/Switch"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import { NodeDetailsLabel } from "@features/graph/details/components/NodeDetailsLabel"
import NodeTagsAccordion from "@features/graph/details/components/NodeTagsAccordion"

// https://tkrisk.pgm-np.spglobal.com/docs/graph_creation/nodes_definition.html#categorical-nodes-definition
// example of "properties" to help understand its structure
// {
//   parents_lst: ["n4", "n3", "n5"], // parent node ids
//   a_sample_value: null,
//   df_cpd: {
//     continuous: [true]
//     expression: ["8*n3+1.2*n5"],
//   },
// }

type DeterministicNodeDetailsProps = {
  nodeId: string
  editMode: GraphEditMode
  graphKey?: string
}

type ExpressionParamRecord = Record<"expression", [string]>
type ContinuousParamRecord = Record<"continuous", [boolean]>
type DeterministicNodeParamRecord = ExpressionParamRecord & ContinuousParamRecord

type DeterministicNodeProperties = NodeProperties<
  DeterministicNodeParamRecord,
  keyof DeterministicNodeParamRecord
>

const DeterministicNodeDetails = ({
  nodeId,
  editMode,
  graphKey,
}: DeterministicNodeDetailsProps) => {
  const graphStore: GraphStoreType = useStoreApi()
  const [node, setNode] = useState<Node>()
  const [expandedExpression, setExpandedExpression] = useLocalStorage(
    `nodeExpressionAccordionState`,
    true,
  )
  const [showCoefficientsAsVariables, setShowCoefficientsAsVariables] = useState<boolean>(true)

  useEffect(() => {
    setNode(findNode(graphStore, nodeId))
  }, [graphStore, nodeId])

  if (!node) {
    return
  }

  const properties: DeterministicNodeProperties = node.data.node?.properties ?? {}
  const sampleValue = properties.a_sample_value

  const label = node.data?.display?.label ?? ""
  const expression = node.data.node?.properties.df_cpd.expression?.[0] ?? ""

  const nodeRows: Row[] = [
    { label: "Type", value: "Deterministic" },
    { label: "Historic Mnemonic", value: node.data.display["Historic Mnem"] },
    { label: "Description", value: node.data.display.Description || "-" },
    { label: "Unit", value: formatValuePythonic("", node.data?.display["Unit Format"]) || "-" },
    { label: "Exclude From Export", value: node.data?.display.exportExcluded ? "yes" : "no" },
    { label: "Link", value: node.data.display.Link ?? "" },
  ]

  if (sampleValue !== null) {
    nodeRows.push({ label: "Sample value", value: sampleValue })
  }

  const handleToggle = function () {
    if (!graphKey) return
    setShowCoefficientsAsVariables(!showCoefficientsAsVariables)
  }

  const handleCoefficientBlur = async (coefficient: string, newValue: number) => {
    if (!graphKey) {
      return
    }

    const updatedExpression = getUpdatedExpression(expression, coefficient, newValue)
    node.data.node.properties.df_cpd.expression = [updatedExpression]

    await graphReplaceNode(graphStore, graphKey, [node])
    setNode(deepCopy(node))
  }

  return (
    <Grid container direction="column" sx={{ p: 1 }} spacing={1}>
      <Grid item>
        <NodeDetailsLabel label={label} />
      </Grid>
      <NodeRowsAccordion nodeRows={nodeRows} />
      <NodeTagsAccordion tags={node.data.display.tags ?? []} />
      <Grid item>
        <Accordion
          expanded={expandedExpression}
          onChange={(event: SyntheticEvent, isExpanded: boolean) => {
            setExpandedExpression(isExpanded)
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 700 }}>Expression</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Grid item sx={{ ml: 1, mb: 1, p: 1, backgroundColor: "rgba(230, 230, 230, .5)" }}>
              <FormControlLabel
                control={<Android12Switch onChange={handleToggle} defaultChecked />}
                label="Show coefficients as variables"
              />
              <Typography>
                {getExpressionWithCoefficients(node.data, showCoefficientsAsVariables).map(
                  (token, i) => (
                    <span
                      key={i}
                      style={
                        token.type === "coeff"
                          ? {
                              background: "rgba(255, 152, 0, 0.4)",
                              fontWeight: 1000,
                            }
                          : {}
                      }
                    >
                      {token.value}
                    </span>
                  ),
                )}
              </Typography>
            </Grid>

            <Grid item>
              {showCoefficientsAsVariables && (
                <CoefficientsTable
                  expression={expression}
                  editable={editMode === GraphEditMode.SCENARIO}
                  onBlur={handleCoefficientBlur}
                />
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
      <ParentChildDetails />
    </Grid>
  )
}

export default DeterministicNodeDetails
